import { useQueryClient } from "@tanstack/react-query";
import {
	createContext,
	type FC,
	type PropsWithChildren,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { useCompany } from "../company/useCompany";
import { useSelectedCompany } from "../company/useSelectedCompany";
import { LinkGen, QueryKeys } from "../LinkGen";
import { useMetaAdsUnsavedMessages } from "./metaAdsUnsavedWork";

const SWITCH_TARGET_MS = 3000;

function isClientScopedDeepRoute(pathname: string): boolean {
	return (
		pathname.includes("/meta-ads/campaigns/edit/") ||
		pathname.includes("/meta-ads/campaigns/drafts/") ||
		/\/meta-ads\/approvals\/\d+/.test(pathname)
	);
}

function confirmUnsaved(messages: string[]): boolean {
	if (messages.length === 0) return true;
	const detail = messages.length === 1 ? messages[0] : messages.join("\n• ");
	return window.confirm(
		`You have unsaved work that will be lost if you switch clients:\n\n• ${detail}\n\nSwitch clients anyway?`,
	);
}

type MetaAdsSwitchContextValue = {
	switchClient: (
		companyId: number,
		options?: { name?: string; skipUnsavedCheck?: boolean },
	) => boolean;
	isSwitching: boolean;
	switchingLabel?: string;
};

const MetaAdsSwitchContext = createContext<MetaAdsSwitchContextValue | null>(
	null,
);

export const MetaAdsSwitchProvider: FC<PropsWithChildren> = ({ children }) => {
	const company = useCompany();
	const [, setSelectedCompany] = useSelectedCompany();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const location = useLocation();
	const emit = useAlertEmit();
	const unsavedMessages = useMetaAdsUnsavedMessages();
	const [switchingTo, setSwitchingTo] = useState<{
		companyId: number;
		name?: string;
	} | null>(null);
	const startedAtRef = useRef<number | null>(null);

	const finishSwitch = useCallback(
		async (companyId: number) => {
			const startedAt = startedAtRef.current ?? performance.now();

			await Promise.allSettled([
				queryClient.prefetchQuery({
					queryKey: QueryKeys.Integrations.MetaAds.Status(companyId),
					queryFn: () => Api.GetMetaAdsStatus().then((r) => r.data),
				}),
				queryClient.prefetchQuery({
					queryKey: QueryKeys.Integrations.MetaAds.AdAccountSingle(companyId),
					queryFn: async () => {
						try {
							return (await Api.GetMetaAdsAdAccount()).data;
						} catch {
							return null;
						}
					},
				}),
				queryClient.invalidateQueries({
					queryKey: ["COMPANY", companyId, "INTEGRATIONS", "META_ADS"],
				}),
				queryClient.invalidateQueries({
					queryKey: QueryKeys.Integrations.MetaAds.Overview(),
				}),
			]);

			const elapsed = performance.now() - startedAt;
			if (elapsed > SWITCH_TARGET_MS) {
				emit({
					type: AlertType.WARNING,
					content: `Client switch took ${Math.round(elapsed / 100) / 10}s (target is under 3s). Data may still be loading.`,
				});
			}

			startedAtRef.current = null;
			setSwitchingTo(null);
		},
		[emit, queryClient],
	);

	useEffect(() => {
		if (!switchingTo) return;
		if (company.id !== switchingTo.companyId) return;
		void finishSwitch(switchingTo.companyId);
	}, [company.id, switchingTo, finishSwitch]);

	useEffect(() => {
		if (!switchingTo) return;
		const timer = window.setTimeout(() => {
			startedAtRef.current = null;
			setSwitchingTo(null);
			emit({
				type: AlertType.WARNING,
				content:
					"Client switch is taking longer than expected. Refresh if data looks stale.",
			});
		}, 8000);
		return () => window.clearTimeout(timer);
	}, [switchingTo, emit]);

	const switchClient = useCallback(
		(
			companyId: number,
			options?: { name?: string; skipUnsavedCheck?: boolean },
		) => {
			if (companyId === company.id) return false;

			if (!options?.skipUnsavedCheck && !confirmUnsaved(unsavedMessages)) {
				return false;
			}

			const deepRoute = isClientScopedDeepRoute(location.pathname);
			if (deepRoute) {
				const proceed = window.confirm(
					"This page belongs to the current client. Switching will take you to the Meta Ads home for the selected client. Continue?",
				);
				if (!proceed) return false;
			}

			startedAtRef.current = performance.now();
			setSwitchingTo({ companyId, name: options?.name });
			setSelectedCompany(companyId);

			if (deepRoute) {
				navigate(LinkGen.MetaAds.Index(), { replace: true });
			}

			return true;
		},
		[
			company.id,
			location.pathname,
			navigate,
			setSelectedCompany,
			unsavedMessages,
		],
	);

	const value = useMemo(
		() => ({
			switchClient,
			isSwitching: switchingTo !== null,
			switchingLabel: switchingTo?.name,
		}),
		[switchClient, switchingTo],
	);

	return (
		<MetaAdsSwitchContext.Provider value={value}>
			{children}
		</MetaAdsSwitchContext.Provider>
	);
};

export function useSwitchMetaAdsClient() {
	const ctx = useContext(MetaAdsSwitchContext);
	if (!ctx) {
		throw new Error(
			"useSwitchMetaAdsClient must be used within MetaAdsSwitchProvider",
		);
	}
	return ctx;
}
